import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { userAPI } from '../lib/api';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Search, GraduationCap, Github, Linkedin, Globe, Loader2 } from 'lucide-react';

export default function Mentors() {
  const [mentors, setMentors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchMentors();
  }, [search]);

  const fetchMentors = async () => {
    setIsLoading(true);
    try {
      const response = await userAPI.getUsers({ 
        role: 'mentor',
        search: search || undefined,
        sort: 'reputation'
      });
      setMentors(response.data);
    } catch (error) {
      console.error('Failed to fetch mentors:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    setSearch(formData.get('search'));
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="mentors-page">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900">Mentors</h1>
          <p className="text-slate-600 mt-1">Connect with experienced developers who can help guide your projects</p>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="mb-8">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              name="search"
              placeholder="Search mentors by name or skill..."
              className="pl-10 bg-white"
            />
          </div>
        </form>

        {/* Mentors Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : mentors.length === 0 ? (
          <div className="text-center py-20">
            <GraduationCap className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No mentors found</h3>
            <p className="text-slate-600">Check back soon for available mentors</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mentors.map((mentor) => (
              <Card key={mentor.user_id} className="card-hover">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Avatar className="w-16 h-16">
                      <AvatarImage src={mentor.picture} />
                      <AvatarFallback className="text-xl bg-purple-100 text-purple-700">
                        {mentor.name?.charAt(0)?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Link to={`/users/${mentor.user_id}`}>
                          <h3 className="text-lg font-semibold text-slate-900 hover:text-indigo-600">
                            {mentor.name}
                          </h3>
                        </Link>
                        <Badge className="bg-purple-100 text-purple-700">
                          <GraduationCap className="w-3 h-3 mr-1" />
                          Mentor
                        </Badge>
                      </div>
                      
                      {mentor.bio && (
                        <p className="text-sm text-slate-600 mt-1 line-clamp-2">
                          {mentor.bio}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Skills */}
                  {mentor.skills?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-4">
                      {mentor.skills.slice(0, 5).map((skill) => (
                        <Badge key={skill} variant="secondary" className="text-xs">
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  )}

                  {/* Social Links */}
                  <div className="flex items-center gap-3 mt-4 pt-4 border-t border-slate-100">
                    {mentor.github_url && (
                      <a
                        href={mentor.github_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-slate-500 hover:text-slate-900"
                      >
                        <Github className="w-5 h-5" />
                      </a>
                    )}
                    {mentor.linkedin_url && (
                      <a
                        href={mentor.linkedin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-slate-500 hover:text-blue-600"
                      >
                        <Linkedin className="w-5 h-5" />
                      </a>
                    )}
                    {mentor.website_url && (
                      <a
                        href={mentor.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-slate-500 hover:text-indigo-600"
                      >
                        <Globe className="w-5 h-5" />
                      </a>
                    )}
                    
                    <div className="ml-auto">
                      <Link to={`/messages/${mentor.user_id}`}>
                        <Button size="sm" variant="outline">
                          Message
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
